import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import VehicleInput from '../components/VehicleInput';
import { INDIAN_STATES } from '../utils/states';

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'Fill Later'];

const Dashboard = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const referralRef = useRef(null);
  const walletRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [walletBalance, setWalletBalance] = useState(0);
  const [showWithdrawal, setShowWithdrawal] = useState(false);
  const [upiId, setUpiId] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [qrUrl, setQrUrl] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    photo: '',
    bloodGroup: '',
    digitalHealthCard: '',
    arogyasriCard: '',
    ayushmanBharatCard: '',
    insuranceCard: '',
    healthProfileFiles: [],
    altContact: '',
    phoneNumber: '',
    vehicle1Name: '',
    vehicle1Number: '',
    vehicle2Name: '',
    vehicle2Number: '',
    vehicle3Name: '',
    vehicle3Number: '',
    familyMembers: [],
    addressState: '',
    addressPincode: '',
    addressArea: '',
  });

  useEffect(() => {
    if (!user || !user._id) {
      navigate('/login');
    } else if (!user.is_registered) {
      navigate('/register-details');
    } else if (user.is_registered && !user.is_paid) {
      navigate('/payment');
    } else {
      setFormData({
        name: user.name || '',
        photo: user.photo || '',
        bloodGroup: user.blood_group || '',
        digitalHealthCard: user.digital_health_card || '',
        arogyasriCard: user.arogyasri_card || '',
        ayushmanBharatCard: user.ayushman_bharat_card || '',
        insuranceCard: user.insurance_card || '',
        healthProfileFiles: user.health_profile_files || [],
        altContact: user.alt_contact || '',
        phoneNumber: user.phone_number || '',
        vehicle1Name: user.vehicle1_name || '',
        vehicle1Number: user.vehicle1_number || '',
        vehicle2Name: user.vehicle2_name || '',
        vehicle2Number: user.vehicle2_number || '',
        vehicle3Name: user.vehicle3_name || '',
        vehicle3Number: user.vehicle3_number || '',
        familyMembers: user.family_members || [],
        addressState: user.address_state || '',
        addressPincode: user.address_pincode || '',
        addressArea: user.address_area || '',
      });
      fetchWalletBalance();
      fetchQrCode();
    }
  }, [user, navigate]);

  useEffect(() => {
    if (location.hash) {
      const timer = setTimeout(() => {
        if (location.hash === '#referral' && referralRef.current) {
          referralRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (location.hash === '#wallet' && walletRef.current) {
          walletRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  const fetchWalletBalance = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/wallet/balance`,
        { withCredentials: true }
      );
      setWalletBalance(data.balance);
    } catch (err) {
      console.error('Failed to fetch wallet balance');
    }
  };

  const fetchQrCode = async () => {
    try {
      setQrLoading(true);
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/profile/qrcode`);
      setQrUrl(data.qr_url);
    } catch (err) { /* not paid or no vehicle */ }
    finally { setQrLoading(false); }
  };

  const downloadQrCode = async () => {
    if (!qrUrl) return;
    try {
      const resp = await axios.get(`${process.env.REACT_APP_BACKEND_URL}${qrUrl}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `MyGaadiNumber-QR-${formData.vehicle1Number || 'code'}.png`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed');
    }
  };

  const handlePhotoUpload = async (e, isFamilyMember = false, index = null) => {
    const file = e.target.files[0];
    if (!file) return;

    const uploadFormData = new FormData();
    uploadFormData.append('photo', file);

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/profile/upload-photo`,
        uploadFormData,
        { withCredentials: true }
      );

      if (isFamilyMember && index !== null) {
        const updatedFamily = [...formData.familyMembers];
        updatedFamily[index].photo = data.photo_url;
        setFormData({ ...formData, familyMembers: updatedFamily });
      } else {
        setFormData({ ...formData, photo: data.photo_url });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to upload photo');
    }
  };

  const handleHealthCardUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/profile/upload-health-card`,
        uploadFormData,
        { withCredentials: true }
      );
      setFormData({ ...formData, digitalHealthCard: data.file_url });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to upload health card');
    }
  };

  const handleDocUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/profile/upload-health-card`,
        uploadFormData,
        { withCredentials: true }
      );
      setFormData({ ...formData, [fieldName]: data.file_url });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to upload document');
    }
  };

  const handleHealthProfileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const remaining = 3 - formData.healthProfileFiles.length;
    if (remaining <= 0) {
      setError('Maximum 3 health profile files allowed');
      return;
    }
    const toUpload = files.slice(0, remaining);
    const uploaded = [];
    for (const file of toUpload) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        const { data } = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/profile/upload-health-card`,
          fd,
          { withCredentials: true }
        );
        uploaded.push(data.file_url);
      } catch (err) {
        setError('Failed to upload a health profile file');
      }
    }
    if (uploaded.length) {
      setFormData(prev => ({ ...prev, healthProfileFiles: [...prev.healthProfileFiles, ...uploaded] }));
    }
  };

  const removeHealthProfileFile = (index) => {
    setFormData(prev => ({
      ...prev,
      healthProfileFiles: prev.healthProfileFiles.filter((_, i) => i !== index),
    }));
  };

  const handleUpdate = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const updateFormData = new FormData();
      updateFormData.append('name', formData.name);
      updateFormData.append('photo', formData.photo);
      updateFormData.append('blood_group', formData.bloodGroup);
      updateFormData.append('digital_health_card', formData.digitalHealthCard);
      updateFormData.append('arogyasri_card', formData.arogyasriCard);
      updateFormData.append('ayushman_bharat_card', formData.ayushmanBharatCard);
      updateFormData.append('insurance_card', formData.insuranceCard);
      updateFormData.append('health_profile_files', JSON.stringify(formData.healthProfileFiles));
      updateFormData.append('alt_contact', formData.altContact);
      updateFormData.append('phone_number', formData.phoneNumber);
      updateFormData.append('vehicle1_name', formData.vehicle1Name);
      updateFormData.append('vehicle1_number', formData.vehicle1Number);
      updateFormData.append('vehicle2_name', formData.vehicle2Name);
      updateFormData.append('vehicle2_number', formData.vehicle2Number);
      updateFormData.append('vehicle3_name', formData.vehicle3Name);
      updateFormData.append('vehicle3_number', formData.vehicle3Number);
      updateFormData.append('family_members', JSON.stringify(formData.familyMembers));
      updateFormData.append('address_state', formData.addressState);
      updateFormData.append('address_pincode', formData.addressPincode);
      updateFormData.append('address_area', formData.addressArea);

      const { data } = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/profile/update`,
        updateFormData,
        { withCredentials: true }
      );

      updateUser(data);
      setSuccess('Profile updated successfully!');
      setIsEditing(false);
    } catch (err) {
      setError('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleWithdrawal = async () => {
    if (walletBalance < 100) {
      setError('Minimum withdrawal amount is ₹100');
      return;
    }

    if (!upiId) {
      setError('Please enter your UPI ID');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/wallet/withdraw`,
        { upi_id: upiId, amount: walletBalance },
        { withCredentials: true }
      );

      setSuccess(`Withdrawal request of ₹${walletBalance.toFixed(2)} submitted successfully! Money will be credited to your UPI ID within 24-48 hours.`);
      setShowWithdrawal(false);
      setUpiId('');
      setWalletBalance(0);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Withdrawal failed');
    } finally {
      setLoading(false);
    }
  };

  const addFamilyMember = () => {
    if (formData.familyMembers.length < 3) {
      setFormData({
        ...formData,
        familyMembers: [...formData.familyMembers, { name: '', bloodGroup: '', digitalHealthCard: '', photo: '', phoneNumber: '' }],
      });
    }
  };

  const removeFamilyMember = (index) => {
    const updated = formData.familyMembers.filter((_, i) => i !== index);
    setFormData({ ...formData, familyMembers: updated });
  };

  const updateFamilyMember = (index, field, value) => {
    const updated = [...formData.familyMembers];
    updated[index][field] = value;
    setFormData({ ...formData, familyMembers: updated });
  };

  return (
    <div className="min-h-screen bg-black py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-[#121214] border border-white/10 rounded-2xl p-8">
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-3xl font-medium">My Profile</h2>
                {!isEditing ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    data-testid="edit-profile-button"
                    className="px-6 py-2.5 bg-[#FF3B30] hover:bg-[#E3242B] rounded-full text-sm font-medium"
                  >
                    Edit Profile
                  </button>
                ) : (
                  <div className="flex gap-3">
                    <button
                      onClick={() => setIsEditing(false)}
                      data-testid="cancel-edit-button"
                      className="px-6 py-2.5 bg-white/10 hover:bg-white/20 rounded-full text-sm font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUpdate}
                      disabled={loading}
                      data-testid="save-profile-button"
                      className="px-6 py-2.5 bg-[#FF3B30] hover:bg-[#E3242B] rounded-full text-sm font-medium disabled:opacity-50"
                    >
                      {loading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                )}
              </div>

              {error && (
                <div className="mb-6 bg-[#FF453A]/10 border border-[#FF453A]/20 rounded-lg p-3 text-[#FF453A] text-sm" data-testid="dashboard-error-message">
                  {error}
                </div>
              )}

              {success && (
                <div className="mb-6 bg-[#32D74B]/10 border border-[#32D74B]/20 rounded-lg p-3 text-[#32D74B] text-sm" data-testid="dashboard-success-message">
                  {success}
                </div>
              )}

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    disabled={!isEditing}
                    data-testid="dashboard-name-input"
                    className="w-full bg-black/50 border border-white/20 rounded-lg p-3 text-white outline-none disabled:opacity-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Blood Group</label>
                  <select
                    value={formData.bloodGroup}
                    onChange={(e) => setFormData({ ...formData, bloodGroup: e.target.value })}
                    disabled={!isEditing}
                    data-testid="dashboard-blood-group-select"
                    className="w-full bg-black/50 border border-white/20 rounded-lg p-3 text-white outline-none disabled:opacity-50"
                  >
                    <option value="">Select Blood Group</option>
                    {BLOOD_GROUPS.map((bg) => (
                      <option key={bg} value={bg}>
                        {bg}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Digital Health Card</label>
                  {isEditing && (
                    <div className="flex gap-3">
                      <input type="file" accept="image/*,.pdf" onChange={(e) => handleDocUpload(e, 'digitalHealthCard')} className="flex-1 bg-black/50 border border-white/20 rounded-lg p-3 text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#FF3B30] file:text-white" />
                      <label className="px-4 py-3 bg-[#0A84FF] hover:bg-[#0070E0] rounded-lg cursor-pointer transition-all flex items-center gap-2 text-sm font-medium whitespace-nowrap">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                        Camera
                        <input type="file" accept="image/*" capture="environment" onChange={(e) => handleDocUpload(e, 'digitalHealthCard')} className="hidden" />
                      </label>
                    </div>
                  )}
                  {formData.digitalHealthCard && <a href={`${process.env.REACT_APP_BACKEND_URL}${formData.digitalHealthCard}`} target="_blank" rel="noreferrer" className="text-[#32D74B] text-sm hover:underline">View uploaded</a>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Arogyasri Card</label>
                  {isEditing && (
                    <div className="flex gap-3">
                      <input type="file" accept="image/*,.pdf" onChange={(e) => handleDocUpload(e, 'arogyasriCard')} className="flex-1 bg-black/50 border border-white/20 rounded-lg p-3 text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#FF3B30] file:text-white" />
                      <label className="px-4 py-3 bg-[#0A84FF] hover:bg-[#0070E0] rounded-lg cursor-pointer transition-all flex items-center gap-2 text-sm font-medium whitespace-nowrap">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                        Camera
                        <input type="file" accept="image/*" capture="environment" onChange={(e) => handleDocUpload(e, 'arogyasriCard')} className="hidden" />
                      </label>
                    </div>
                  )}
                  {formData.arogyasriCard && <a href={`${process.env.REACT_APP_BACKEND_URL}${formData.arogyasriCard}`} target="_blank" rel="noreferrer" className="text-[#32D74B] text-sm hover:underline">View uploaded</a>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Ayushman Bharat Card</label>
                  {isEditing && (
                    <div className="flex gap-3">
                      <input type="file" accept="image/*,.pdf" onChange={(e) => handleDocUpload(e, 'ayushmanBharatCard')} className="flex-1 bg-black/50 border border-white/20 rounded-lg p-3 text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#FF3B30] file:text-white" />
                      <label className="px-4 py-3 bg-[#0A84FF] hover:bg-[#0070E0] rounded-lg cursor-pointer transition-all flex items-center gap-2 text-sm font-medium whitespace-nowrap">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                        Camera
                        <input type="file" accept="image/*" capture="environment" onChange={(e) => handleDocUpload(e, 'ayushmanBharatCard')} className="hidden" />
                      </label>
                    </div>
                  )}
                  {formData.ayushmanBharatCard && <a href={`${process.env.REACT_APP_BACKEND_URL}${formData.ayushmanBharatCard}`} target="_blank" rel="noreferrer" className="text-[#32D74B] text-sm hover:underline">View uploaded</a>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Insurance Document</label>
                  {isEditing && (
                    <div className="flex gap-3">
                      <input type="file" accept="image/*,.pdf" onChange={(e) => handleDocUpload(e, 'insuranceCard')} className="flex-1 bg-black/50 border border-white/20 rounded-lg p-3 text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#FF3B30] file:text-white" />
                      <label className="px-4 py-3 bg-[#0A84FF] hover:bg-[#0070E0] rounded-lg cursor-pointer transition-all flex items-center gap-2 text-sm font-medium whitespace-nowrap">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                        Camera
                        <input type="file" accept="image/*" capture="environment" onChange={(e) => handleDocUpload(e, 'insuranceCard')} className="hidden" />
                      </label>
                    </div>
                  )}
                  {formData.insuranceCard && <a href={`${process.env.REACT_APP_BACKEND_URL}${formData.insuranceCard}`} target="_blank" rel="noreferrer" className="text-[#32D74B] text-sm hover:underline">View uploaded</a>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Health Profile (Optional - Max 3 files)</label>
                  {isEditing && formData.healthProfileFiles.length < 3 && (
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      multiple
                      onChange={handleHealthProfileUpload}
                      data-testid="dashboard-health-profile-upload"
                      className="w-full bg-black/50 border border-white/20 rounded-lg p-3 text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#FF3B30] file:text-white"
                    />
                  )}
                  {formData.healthProfileFiles.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {formData.healthProfileFiles.map((url, i) => (
                        <div key={i} className="flex items-center justify-between bg-black/30 border border-white/10 rounded-lg px-3 py-2">
                          <a href={`${process.env.REACT_APP_BACKEND_URL}${url}`} target="_blank" rel="noreferrer" className="text-[#32D74B] text-sm hover:underline truncate flex-1">File {i + 1}</a>
                          {isEditing && (
                            <button type="button" onClick={() => removeHealthProfileFile(i)} className="ml-2 text-[#FF3B30] hover:text-red-400 text-sm font-medium">Remove</button>
                          )}
                        </div>
                      ))}
                      <p className="text-[#A1A1A6] text-xs">{formData.healthProfileFiles.length}/3 files uploaded</p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Alternative Contact</label>
                  <input
                    type="tel"
                    value={formData.altContact}
                    onChange={(e) => setFormData({ ...formData, altContact: e.target.value })}
                    disabled={!isEditing}
                    data-testid="dashboard-alt-contact-input"
                    className="w-full bg-black/50 border border-white/20 rounded-lg p-3 text-white outline-none disabled:opacity-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Applicant Phone Number (Private)</label>
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                    disabled={!isEditing}
                    data-testid="dashboard-phone-number-input"
                    placeholder="+91 XXXXXXXXXX"
                    className="w-full bg-black/50 border border-white/20 rounded-lg p-3 text-white outline-none disabled:opacity-50"
                  />
                  <p className="text-[#A1A1A6] text-xs mt-1">Not visible in search results - only visible to admin</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Photo</label>
                  {isEditing && (
                    <div className="flex gap-3">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handlePhotoUpload(e)}
                        data-testid="dashboard-photo-upload"
                        className="flex-1 bg-black/50 border border-white/20 rounded-lg p-3 text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#FF3B30] file:text-white"
                      />
                      <label className="px-4 py-3 bg-[#0A84FF] hover:bg-[#0070E0] rounded-lg cursor-pointer transition-all flex items-center gap-2 text-sm font-medium whitespace-nowrap">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Camera
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          onChange={(e) => handlePhotoUpload(e)}
                          className="hidden"
                        />
                      </label>
                    </div>
                  )}
              {formData.photo && (
                    <img src={formData.photo.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${formData.photo}` : formData.photo} alt="Profile" className="mt-4 w-32 h-32 object-cover rounded-lg" />
                  )}
                </div>

                <div className="border-t border-white/10 pt-6">
                  <h3 className="text-xl font-medium mb-4">Vehicle Details</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder="Vehicle 1 Name"
                        value={formData.vehicle1Name}
                        onChange={(e) => setFormData({ ...formData, vehicle1Name: e.target.value })}
                        disabled={!isEditing}
                        data-testid="dashboard-vehicle1-name-input"
                        className="bg-black/50 border border-white/20 rounded-lg p-3 text-white outline-none disabled:opacity-50"
                      />
                      <VehicleInput
                        value={formData.vehicle1Number}
                        onChange={(val) => setFormData({ ...formData, vehicle1Number: val })}
                        placeholder="Vehicle 1 Number"
                        disabled={!isEditing}
                        dataTestId="dashboard-vehicle1-number-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder="Vehicle 2 Name (Optional)"
                        value={formData.vehicle2Name}
                        onChange={(e) => setFormData({ ...formData, vehicle2Name: e.target.value })}
                        disabled={!isEditing}
                        data-testid="dashboard-vehicle2-name-input"
                        className="bg-black/50 border border-white/20 rounded-lg p-3 text-white outline-none disabled:opacity-50"
                      />
                      <VehicleInput
                        value={formData.vehicle2Number}
                        onChange={(val) => setFormData({ ...formData, vehicle2Number: val })}
                        placeholder="Vehicle 2 Number"
                        disabled={!isEditing}
                        dataTestId="dashboard-vehicle2-number-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder="Vehicle 3 Name (Optional)"
                        value={formData.vehicle3Name}
                        onChange={(e) => setFormData({ ...formData, vehicle3Name: e.target.value })}
                        disabled={!isEditing}
                        data-testid="dashboard-vehicle3-name-input"
                        className="bg-black/50 border border-white/20 rounded-lg p-3 text-white outline-none disabled:opacity-50"
                      />
                      <VehicleInput
                        value={formData.vehicle3Number}
                        onChange={(val) => setFormData({ ...formData, vehicle3Number: val })}
                        placeholder="Vehicle 3 Number"
                        disabled={!isEditing}
                        dataTestId="dashboard-vehicle3-number-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t border-white/10 pt-6">
                  <h3 className="text-xl font-medium mb-4">Address (Private)</h3>
                  <div className="bg-[#FF9F0A]/10 border border-[#FF9F0A]/20 rounded-lg p-3 mb-4">
                    <p className="text-[#FF9F0A] text-xs">
                      Your address is private and NOT visible in emergency searches
                    </p>
                  </div>
                  <div className="space-y-4">
                    <select
                      value={formData.addressState}
                      onChange={(e) => setFormData({ ...formData, addressState: e.target.value })}
                      disabled={!isEditing}
                      data-testid="dashboard-address-state-select"
                      className="w-full bg-black/50 border border-white/20 rounded-lg p-3 text-white outline-none disabled:opacity-50"
                    >
                      <option value="">Select State</option>
                      {INDIAN_STATES.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      placeholder="Pincode"
                      value={formData.addressPincode}
                      onChange={(e) => setFormData({ ...formData, addressPincode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                      disabled={!isEditing}
                      data-testid="dashboard-address-pincode-input"
                      maxLength={6}
                      className="w-full bg-black/50 border border-white/20 rounded-lg p-3 text-white outline-none disabled:opacity-50"
                    />
                    <textarea
                      placeholder="Area/Locality"
                      value={formData.addressArea}
                      onChange={(e) => setFormData({ ...formData, addressArea: e.target.value })}
                      disabled={!isEditing}
                      data-testid="dashboard-address-area-input"
                      rows={3}
                      className="w-full bg-black/50 border border-white/20 rounded-lg p-3 text-white outline-none disabled:opacity-50 resize-none"
                    />
                  </div>
                </div>

                <div className="border-t border-white/10 pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-medium">Family Members</h3>
                    {isEditing && formData.familyMembers.length < 3 && (
                      <button
                        onClick={addFamilyMember}
                        data-testid="dashboard-add-family-button"
                        className="px-4 py-2 bg-[#FF3B30] hover:bg-[#E3242B] rounded-full text-sm"
                      >
                        Add Member
                      </button>
                    )}
                  </div>
                  {formData.familyMembers.length === 0 ? (
                    <p className="text-[#A1A1A6] text-center py-8">No family members added</p>
                  ) : (
                    <div className="space-y-4">
                      {formData.familyMembers.map((member, index) => (
                        <div key={index} className="bg-[#0A0A0B] p-4 rounded-xl border border-white/10">
                          <div className="flex justify-between items-center mb-3">
                            <h4 className="font-medium">Member {index + 1}</h4>
                            {isEditing && (
                              <button
                                onClick={() => removeFamilyMember(index)}
                                data-testid={`dashboard-remove-family-${index}-button`}
                                className="text-[#FF453A] text-sm hover:underline"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                          <div className="space-y-3">
                            <input
                              type="text"
                              placeholder="Name"
                              value={member.name}
                              onChange={(e) => updateFamilyMember(index, 'name', e.target.value)}
                              disabled={!isEditing}
                              data-testid={`dashboard-family-${index}-name-input`}
                              className="w-full bg-black/50 border border-white/20 rounded-lg p-3 text-white outline-none disabled:opacity-50"
                            />
                            <select
                              value={member.bloodGroup}
                              onChange={(e) => updateFamilyMember(index, 'bloodGroup', e.target.value)}
                              disabled={!isEditing}
                              data-testid={`dashboard-family-${index}-blood-group-select`}
                              className="w-full bg-black/50 border border-white/20 rounded-lg p-3 text-white outline-none disabled:opacity-50"
                            >
                              <option value="">Blood Group</option>
                              {BLOOD_GROUPS.map((bg) => (
                                <option key={bg} value={bg}>
                                  {bg}
                                </option>
                              ))}
                            </select>
                            <input
                              type="text"
                              placeholder="Digital Health Card (Optional)"
                              value={member.digitalHealthCard || ''}
                              onChange={(e) => updateFamilyMember(index, 'digitalHealthCard', e.target.value)}
                              disabled={!isEditing}
                              data-testid={`dashboard-family-${index}-health-card-input`}
                              className="w-full bg-black/50 border border-white/20 rounded-lg p-3 text-white outline-none disabled:opacity-50"
                            />
                            {member.photo && (
                              <img src={member.photo.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${member.photo}` : member.photo} alt={member.name} className="w-20 h-20 object-cover rounded-lg" />
                            )}
                            {isEditing && (
                              <>
                              <label className="block text-sm font-medium mb-1 text-gray-300">Choose Family Member Photo</label>
                              <div className="flex gap-3">
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => handlePhotoUpload(e, true, index)}
                                  data-testid={`dashboard-family-${index}-photo-upload`}
                                  className="flex-1 bg-black/50 border border-white/20 rounded-lg p-3 text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#FF3B30] file:text-white"
                                  title="Choose Family Member Photo"
                                />
                                <label className="px-4 py-3 bg-[#0A84FF] hover:bg-[#0070E0] rounded-lg cursor-pointer transition-all flex items-center gap-2 text-sm font-medium whitespace-nowrap">
                                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                  Camera
                                  <input
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    onChange={(e) => handlePhotoUpload(e, true, index)}
                                    className="hidden"
                                  />
                                </label>
                              </div>
                              </>
                            )}
                            {isEditing && (
                              <input
                                type="tel"
                                placeholder="Applicant Phone Number (Optional - Private)"
                                value={member.phoneNumber || ''}
                                onChange={(e) => updateFamilyMember(index, 'phoneNumber', e.target.value)}
                                disabled={!isEditing}
                                data-testid={`dashboard-family-${index}-phone-input`}
                                className="w-full bg-black/50 border border-white/20 rounded-lg p-3 text-white outline-none disabled:opacity-50"
                              />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {qrUrl && (
              <div className="bg-[#121214] border border-white/10 rounded-2xl p-6">
                <h3 className="text-xl font-medium mb-4">Your QR Code</h3>
                <p className="text-[#A1A1A6] text-sm mb-4">Scan this QR code with Google Lens or any scanner to find your emergency details</p>
                <div className="flex flex-col items-center">
                  <div className="bg-white p-4 rounded-2xl">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${qrUrl}`}
                      alt="Your QR Code"
                      data-testid="user-qr-code"
                      className="w-56 h-56"
                    />
                  </div>
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={downloadQrCode}
                      data-testid="download-qr-button"
                      className="px-6 py-3 bg-[#32D74B] hover:bg-[#2AB840] rounded-full text-sm font-medium text-black transition-all flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download QR
                    </button>
                    <button
                      onClick={() => {
                        const shareUrl = `https://mygaadinumber.com/search?vehicle=${formData.vehicle1Number}`;
                        if (navigator.share) {
                          navigator.share({ title: 'My Vehicle QR', text: `Scan or click to find my emergency details: ${shareUrl}`, url: shareUrl });
                        } else {
                          navigator.clipboard.writeText(shareUrl);
                          alert('Link copied!');
                        }
                      }}
                      data-testid="share-qr-button"
                      className="px-6 py-3 bg-[#0A84FF] hover:bg-[#0070E0] rounded-full text-sm font-medium transition-all flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                      </svg>
                      Share Link
                    </button>
                  </div>
                </div>
              </div>
            )}

            {qrLoading && (
              <div className="bg-[#121214] border border-white/10 rounded-2xl p-6 text-center">
                <p className="text-[#A1A1A6]">Generating your QR code...</p>
              </div>
            )}

            <div className="bg-[#121214] border border-white/10 rounded-2xl p-6" ref={walletRef}>
              <h3 className="text-xl font-medium mb-4">Wallet</h3>
              <div className="text-center py-6">
                <p className="text-[#A1A1A6] text-sm mb-2">Available Balance</p>
                <p className="text-4xl font-medium text-[#32D74B]">₹{walletBalance.toFixed(2)}</p>
              </div>
              <button
                onClick={() => setShowWithdrawal(true)}
                disabled={walletBalance < 100}
                data-testid="request-withdrawal-button"
                className="w-full px-6 py-3 bg-[#FF3B30] hover:bg-[#E3242B] rounded-full font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Request Withdrawal
              </button>
              {walletBalance < 100 && (
                <p className="text-[#A1A1A6] text-xs text-center mt-3">
                  Minimum balance ₹100 required for withdrawal
                </p>
              )}
            </div>

            <div className="bg-[#121214] border border-white/10 rounded-2xl p-6" ref={referralRef}>
              <h3 className="text-xl font-medium mb-4">Your Referral Code</h3>
              <div className="bg-[#0A0A0B] p-4 rounded-xl border border-white/10 flex items-center justify-between">
                <p className="text-2xl font-medium text-[#FF3B30]" data-testid="user-referral-code">{user?.referral_code}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(user?.referral_code || '');
                      setCopySuccess(true);
                      setTimeout(() => setCopySuccess(false), 2000);
                    }}
                    data-testid="copy-referral-button"
                    className="px-4 py-2.5 bg-white/10 hover:bg-white/20 rounded-full text-sm font-medium transition-all flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    {copySuccess ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={() => {
                      const text = `Join MyGaadiNumber - Register your vehicle and help save lives in emergencies! Use my referral code: ${user?.referral_code} to get ₹25 off. Sign up now at https://mygaadinumber.com`;
                      if (navigator.share) {
                        navigator.share({ title: 'MyGaadiNumber Referral', text, url: 'https://mygaadinumber.com' });
                      } else {
                        navigator.clipboard.writeText(text);
                        alert('Referral message copied to clipboard!');
                      }
                    }}
                    data-testid="share-referral-button"
                    className="px-4 py-2.5 bg-[#0A84FF] hover:bg-[#0070E0] rounded-full text-sm font-medium transition-all flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    Share
                  </button>
                </div>
              </div>
              <p className="text-[#A1A1A6] text-sm mt-4">
                Share this code with friends. Earn ₹50 for every successful referral!
              </p>
            </div>
          </div>
        </div>

        {showWithdrawal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-xl flex items-center justify-center z-50 p-6">
            <div className="bg-[#121214] border border-white/10 rounded-2xl p-8 max-w-md w-full">
              <h3 className="text-2xl font-medium mb-6">Withdraw Funds</h3>
              
              <div className="bg-[#0A0A0B] p-6 rounded-xl border border-white/10 mb-6">
                <p className="text-[#A1A1A6] text-sm mb-2">Withdrawal Amount</p>
                <p className="text-4xl font-medium text-[#32D74B]">₹{walletBalance.toFixed(2)}</p>
                <p className="text-[#86868B] text-xs mt-2">Your entire wallet balance will be withdrawn</p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">UPI ID *</label>
                  <input
                    type="text"
                    value={upiId}
                    onChange={(e) => setUpiId(e.target.value)}
                    placeholder="yourname@upi"
                    data-testid="withdrawal-upi-input"
                    className="w-full bg-black/50 border border-white/20 focus:border-[#FF3B30] rounded-lg p-3 text-white outline-none"
                  />
                </div>
                
                <div className="bg-[#FF9F0A]/10 border border-[#FF9F0A]/20 rounded-lg p-4">
                  <p className="text-[#FF9F0A] text-sm">
                    <strong>Important:</strong> Money will be credited to your provided UPI ID within 24-48 hours. Your wallet balance will become ₹0 immediately after submitting this request.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4 mt-6">
                <button
                  onClick={() => {
                    setShowWithdrawal(false);
                    setError('');
                  }}
                  data-testid="cancel-withdrawal-button"
                  className="flex-1 px-6 py-3 bg-white/10 hover:bg-white/20 rounded-full font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleWithdrawal}
                  disabled={loading}
                  data-testid="submit-withdrawal-button"
                  className="flex-1 px-6 py-3 bg-[#FF3B30] hover:bg-[#E3242B] rounded-full font-medium disabled:opacity-50"
                >
                  {loading ? 'Processing...' : 'Withdraw ₹' + walletBalance.toFixed(2)}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
